import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Plus, Settings, ChevronLeft, ChevronRight, X, UserPlus, Eye } from 'lucide-react';
import api from '../../api';

const getArgentinaToday = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
};

// Returns true if the appointment is today (at or after current time) or in the future
const isApptFutureOrNow = (appt) => {
  const argDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
  const argTime = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit' });
  return appt.date > argDate || (appt.date === argDate && appt.time >= argTime);
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const getWeekDays = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00Z');
  const day = date.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(date);
    d.setUTCDate(date.getUTCDate() + diff + i);
    return d.toISOString().split('T')[0];
  });
};

const getMonthDays = (year, month) => {
  const firstDay = new Date(Date.UTC(year, month, 1));
  const lastDay = new Date(Date.UTC(year, month + 1, 0));
  const startDay = new Date(firstDay);
  const dow = firstDay.getUTCDay();
  startDay.setUTCDate(startDay.getUTCDate() - (dow === 0 ? 6 : dow - 1));
  const days = [];
  const cur = new Date(startDay);
  while (cur <= lastDay || days.length % 7 !== 0) {
    days.push(cur.toISOString().split('T')[0]);
    cur.setUTCDate(cur.getUTCDate() + 1);
    if (days.length > 42) break;
  }
  return days;
};

const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function Appointments() {
  const navigate = useNavigate();
  const { setShowMenu, setNewApptCount } = useOutletContext() || {};
  const { user } = useAuth();

  const isEmployee = user?.user_type === 'employee';
  const businessId = isEmployee ? user?.parent_user_id : user?.id;
  const staffFilter = isEmployee ? user?.id : null;

  const today = getArgentinaToday();
  const [viewMode, setViewMode] = useState('day');
  const [selectedDate, setSelectedDate] = useState(today);
  const [allAppts, setAllAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);

  // Form
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formDate, setFormDate] = useState(today);
  const [formTime, setFormTime] = useState('');
  const [formService, setFormService] = useState('');
  const [formStaff, setFormStaff] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAppts = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const params = {};
      if (staffFilter) params.staff_user_id = staffFilter;
      const r = await api.get(`/appointments/${businessId}`, { params });
      setAllAppts(r.data || []);
      const newCnt = (r.data || []).filter(a => a.is_new && isApptFutureOrNow(a)).length;
      if (setNewApptCount) setNewApptCount(newCnt);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [businessId, staffFilter]);

  useEffect(() => { loadAppts(); }, [loadAppts]);

  useEffect(() => {
    if (!businessId) return;
    api.get(`/appointment-services/${businessId}`).then(r => setServices(r.data || [])).catch(() => {});
    api.get(`/appointment-staff/${businessId}`).then(r => setStaffList(r.data || [])).catch(() => {});
  }, [businessId]);

  // Day view logic
  const dayAppts = allAppts.filter(a => a.date === selectedDate).sort((a, b) => a.time.localeCompare(b.time));

  // Week view logic
  const weekDays = getWeekDays(selectedDate);
  const apptsByDate = weekDays.reduce((acc, d) => {
    acc[d] = allAppts.filter(a => a.date === d).sort((a, b) => a.time.localeCompare(b.time));
    return acc;
  }, {});

  // Month view logic
  const selDateObj = new Date(selectedDate + 'T12:00:00Z');
  const monthDays = getMonthDays(selDateObj.getUTCFullYear(), selDateObj.getUTCMonth());
  const apptsByDateMap = allAppts.reduce((acc, a) => {
    if (!acc[a.date]) acc[a.date] = [];
    acc[a.date].push(a);
    return acc;
  }, {});

  const navigateDay = (dir) => {
    const d = new Date(selectedDate + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + dir);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const navigateWeek = (dir) => {
    const d = new Date(selectedDate + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + dir * 7);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const navigateMonth = (dir) => {
    const d = new Date(selectedDate + 'T12:00:00Z');
    d.setUTCMonth(d.getUTCMonth() + dir);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const openCreate = () => {
    setFormName(''); setFormPhone(''); setFormDate(selectedDate); setFormTime('');
    setFormService(''); setFormStaff(''); setFormNotes(''); setShowModal(true); setSelectedAppt(null);
  };

  const openDetail = async (a) => {
    setSelectedAppt(a);
    if (a.is_new) {
      try { await api.put(`/appointments/${a.id}/mark-seen`); loadAppts(); } catch (_) {}
    }
  };

  const handleCreate = async () => {
    if (!formName || !formDate || !formTime) { alert('Nombre, fecha y hora son obligatorios'); return; }
    setSaving(true);
    try {
      const svc = services.find(s => s.id === formService);
      const st = staffList.find(s => s.id === formStaff);
      await api.post('/appointments', {
        business_user_id: businessId,
        client_name: formName,
        client_phone: formPhone || null,
        date: formDate,
        time: formTime,
        service_id: formService || null,
        service_name: svc?.name || null,
        service_duration: svc?.duration_minutes || null,
        staff_name: st?.staff_name || null,
        staff_color: st?.color || null,
        staff_user_id: st?.linked_user_id || null,
        notes: formNotes || null
      });
      setShowModal(false);
      loadAppts();
    } catch (e) { alert('Error al guardar turno'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar turno?')) return;
    await api.delete(`/appointments/${id}`);
    setSelectedAppt(null);
    loadAppts();
  };

  const handleAddToClients = async (a) => {
    if (!a.client_name) return;
    try {
      await api.post('/clients', { business_user_id: businessId, full_name: a.client_name, phone: a.client_phone || '' });
      alert(`${a.client_name} agregado a Clientes`);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const apptCard = (a, compact = false) => (
    <div key={a.id} data-testid={`appt-card-${a.id}`}
      onClick={() => openDetail(a)}
      style={{
        borderLeft: `4px solid ${a.staff_color || 'var(--accent)'}`,
        background: 'var(--card-bg)', borderRadius: 8, padding: compact ? '6px 10px' : '10px 14px',
        marginBottom: compact ? 4 : 8, cursor: 'pointer', position: 'relative'
      }}>
      {a.is_new && (
        <span style={{
          position: 'absolute', top: 4, right: 4, background: 'var(--danger)', color: '#fff',
          fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px'
        }}>NUEVO</span>
      )}
      {!compact && <div style={{ fontSize: '0.7rem', color: 'var(--text2)', marginBottom: 2 }}>{a.time}{a.end_time ? ` - ${a.end_time}` : ''}</div>}
      <div style={{ fontWeight: 700, fontSize: compact ? '0.8rem' : '0.95rem' }}>{a.client_name}</div>
      {!compact && a.service_name && <div style={{ fontSize: '0.8rem', color: 'var(--accent)' }}>{a.service_name}</div>}
      {!compact && a.staff_name && <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>{a.staff_name}</div>}
    </div>
  );

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate('/business')} data-testid="back-btn">←</button>
        <h1 className="header-title" style={{ flex: 1 }}>
          Turnos
          {allAppts.filter(a => a.is_new && isApptFutureOrNow(a)).length > 0 && (
            <span style={{
              marginLeft: 8, background: 'var(--danger)', color: '#fff', fontSize: 11, fontWeight: 700,
              borderRadius: '50%', width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
            }}>{allAppts.filter(a => a.is_new && isApptFutureOrNow(a)).length}</span>
          )}
        </h1>
        <button className="icon-btn" onClick={() => navigate('/business/appointments-config')} title="Configurar"><Settings size={20} /></button>
        <button className="icon-btn" onClick={openCreate} data-testid="new-appt-btn"><Plus size={24} /></button>
      </div>

      {/* View Mode Tabs */}
      <div className="period-tabs" style={{ paddingTop: 0, paddingBottom: 0 }}>
        {[['day','Día'],['week','Semana'],['month','Mes']].map(([k,l]) => (
          <button key={k} className={`period-tab ${viewMode === k ? 'active' : ''}`} onClick={() => setViewMode(k)}>{l}</button>
        ))}
      </div>

      {/* DAY VIEW */}
      {viewMode === 'day' && (
        <div className="content-scroll">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <button className="icon-btn" onClick={() => navigateDay(-1)}><ChevronLeft size={20} /></button>
            <span style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>
              {selectedDate === today ? 'Hoy — ' : ''}{fmtDate(selectedDate)}
            </span>
            <button className="icon-btn" onClick={() => navigateDay(1)}><ChevronRight size={20} /></button>
            <button className="btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={() => setSelectedDate(today)}>Hoy</button>
          </div>
          {loading ? <div className="spinner" /> : dayAppts.length === 0 ? (
            <div className="empty-state"><p>No hay turnos para este día.</p></div>
          ) : (
            <div style={{ padding: '0 4px' }}>
              {dayAppts.map(a => apptCard(a))}
            </div>
          )}
        </div>
      )}

      {/* WEEK VIEW */}
      {viewMode === 'week' && (
        <div className="content-scroll">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <button className="icon-btn" onClick={() => navigateWeek(-1)}><ChevronLeft size={20} /></button>
            <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem' }}>
              {fmtDate(weekDays[0])} – {fmtDate(weekDays[6])}
            </span>
            <button className="icon-btn" onClick={() => navigateWeek(1)}><ChevronRight size={20} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {DAY_LABELS.map(l => (
              <div key={l} style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text2)', paddingBottom: 4 }}>{l}</div>
            ))}
            {weekDays.map(d => {
              const isToday = d === today;
              const isSelected = d === selectedDate;
              const dayA = apptsByDate[d] || [];
              return (
                <div key={d} onClick={() => { setSelectedDate(d); setViewMode('day'); }}
                  style={{
                    minHeight: 80, background: isSelected ? 'rgba(93,230,200,0.1)' : 'var(--card-bg)',
                    borderRadius: 8, padding: '4px', cursor: 'pointer',
                    border: isToday ? '2px solid var(--accent)' : '1px solid var(--border)'
                  }}>
                  <div style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--accent)' : 'var(--text)' }}>
                    {new Date(d + 'T12:00:00Z').getUTCDate()}
                  </div>
                  {dayA.slice(0, 3).map(a => (
                    <div key={a.id} style={{
                      height: 6, borderRadius: 3, background: a.staff_color || 'var(--accent)',
                      marginBottom: 2
                    }} title={`${a.time} ${a.client_name}`} />
                  ))}
                  {dayA.length > 3 && <div style={{ fontSize: '0.6rem', color: 'var(--text2)', textAlign: 'center' }}>+{dayA.length - 3}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MONTH VIEW */}
      {viewMode === 'month' && (
        <div className="content-scroll">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <button className="icon-btn" onClick={() => navigateMonth(-1)}><ChevronLeft size={20} /></button>
            <span style={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>
              {MONTH_NAMES[selDateObj.getUTCMonth()]} {selDateObj.getUTCFullYear()}
            </span>
            <button className="icon-btn" onClick={() => navigateMonth(1)}><ChevronRight size={20} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {DAY_LABELS.map(l => (
              <div key={l} style={{ textAlign: 'center', fontSize: '0.65rem', fontWeight: 700, color: 'var(--text2)', paddingBottom: 4 }}>{l}</div>
            ))}
            {monthDays.map(d => {
              const isCurrentMonth = new Date(d + 'T12:00:00Z').getUTCMonth() === selDateObj.getUTCMonth();
              const isToday = d === today;
              const dayA = apptsByDateMap[d] || [];
              return (
                <div key={d} onClick={() => { setSelectedDate(d); setViewMode('day'); }}
                  style={{
                    minHeight: 48, background: isToday ? 'rgba(93,230,200,0.15)' : 'var(--card-bg)',
                    borderRadius: 6, padding: '3px', cursor: 'pointer', opacity: isCurrentMonth ? 1 : 0.4,
                    border: isToday ? '2px solid var(--accent)' : '1px solid var(--border)'
                  }}>
                  <div style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: isToday ? 700 : 400 }}>
                    {new Date(d + 'T12:00:00Z').getUTCDate()}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                    {dayA.slice(0, 4).map(a => (
                      <div key={a.id} style={{ width: 6, height: 6, borderRadius: '50%', background: a.staff_color || 'var(--accent)' }} />
                    ))}
                  </div>
                  {dayA.length > 0 && <div style={{ textAlign: 'center', fontSize: '0.55rem', color: 'var(--text2)' }}>{dayA.length}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* New Appointment Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" style={{ maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ flex: 1, margin: 0 }}>Nuevo Turno</h3>
              <button className="icon-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="form-group"><label className="label">Nombre y Apellido *</label>
              <input className="input" data-testid="appt-name-input" placeholder="Cliente" value={formName} onChange={e => setFormName(e.target.value)} /></div>
            <div className="form-group"><label className="label">Celular</label>
              <input className="input" data-testid="appt-phone-input" type="tel" placeholder="351 000 0000" value={formPhone} onChange={e => setFormPhone(e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}><label className="label">Fecha *</label>
                <input className="input" data-testid="appt-date-input" type="date" value={formDate} onChange={e => setFormDate(e.target.value)} /></div>
              <div className="form-group" style={{ flex: 1 }}><label className="label">Hora *</label>
                <input className="input" data-testid="appt-time-input" type="time" value={formTime} onChange={e => setFormTime(e.target.value)} /></div>
            </div>
            {services.length > 0 && (
              <div className="form-group"><label className="label">Servicio</label>
                <select className="input" data-testid="appt-service-select" value={formService} onChange={e => setFormService(e.target.value)}>
                  <option value="">Sin servicio</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes}min)</option>)}
                </select></div>
            )}
            {staffList.length > 0 && (
              <div className="form-group"><label className="label">Barbero / Estilista</label>
                <select className="input" data-testid="appt-staff-select" value={formStaff} onChange={e => setFormStaff(e.target.value)}>
                  <option value="">Sin asignar</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.staff_name}</option>)}
                </select></div>
            )}
            <div className="form-group"><label className="label">Notas</label>
              <textarea className="input" rows={2} placeholder="Observaciones..." value={formNotes} onChange={e => setFormNotes(e.target.value)} /></div>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn-primary" data-testid="save-appt-btn" onClick={handleCreate} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Detail Modal */}
      {selectedAppt && (
        <div className="modal-overlay" onClick={() => setSelectedAppt(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ width: 4, height: 40, borderRadius: 2, background: selectedAppt.staff_color || 'var(--accent)', marginRight: 12 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{selectedAppt.client_name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>{fmtDate(selectedAppt.date)} — {selectedAppt.time}{selectedAppt.end_time ? ` a ${selectedAppt.end_time}` : ''}</div>
              </div>
              <button className="icon-btn" onClick={() => setSelectedAppt(null)}><X size={20} /></button>
            </div>
            {selectedAppt.client_phone && <div className="card-row"><span className="card-row-label">Celular</span><span>{selectedAppt.client_phone}</span></div>}
            {selectedAppt.service_name && <div className="card-row"><span className="card-row-label">Servicio</span><span>{selectedAppt.service_name}</span></div>}
            {selectedAppt.staff_name && <div className="card-row"><span className="card-row-label">Profesional</span><span style={{ color: selectedAppt.staff_color }}>{selectedAppt.staff_name}</span></div>}
            {selectedAppt.notes && <div className="card-row"><span className="card-row-label">Notas</span><span>{selectedAppt.notes}</span></div>}
            {selectedAppt.booked_by_client && <div style={{ background: 'rgba(93,230,200,0.1)', borderRadius: 6, padding: '6px 10px', marginTop: 8, fontSize: '0.8rem', color: 'var(--accent)' }}>Reservado por el cliente online</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => handleAddToClients(selectedAppt)}>
                <UserPlus size={14} style={{ marginRight: 4 }} />Agregar como Cliente
              </button>
              <button style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}
                onClick={() => handleDelete(selectedAppt.id)}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
